import UI from "@mile-meeting/mile-ui";
import { useEffect, useRef, useState } from "react";
import { TTimeSlotMinAttendeesPolicy } from "../MinimumAttendeesSettingList/MinimumAttendeesSettingItemList";
import { Container, TimeContainer, InputForm, TimeDropdownContainer, TimeDropdownItem, SettingSection, InputContainer, ButtonSection } from "./MinimumAttendeesSettingItem.styles";
import { TMeetingRoom } from "apis/meetings/meeting-room";
import { ErrorType } from "../../types";


const MinimumAttendeesSettingItem = ({
  idx,
  timeSlotMinAttenancePolicyInfo,
  onDelete,
  onChange,
  room,
}: {
  idx: number;
  timeSlotMinAttenancePolicyInfo: TTimeSlotMinAttendeesPolicy;
  onDelete: (deleteIdx: number) => void;
  onChange: (updateIdx: number, updatePolicy: TTimeSlotMinAttendeesPolicy) => void;
  room: TMeetingRoom;
}) => {
  const defaultMinAttendeeNum = Math.ceil(room.capacity * 0.3);
  const [minAttendeeNum, setMinAttendeeNum] = useState<number | undefined>(defaultMinAttendeeNum);
  const isMinAttendeeNumError = (minAttendeeNum !== undefined ? (minAttendeeNum <= 1 || minAttendeeNum > room.capacity) : false)

  const handleChangeMinAttendeesInput = (value: string) => {
    if (/^\d*$/.test(value)) {
      if (value === "") {
        setMinAttendeeNum(undefined);  
      } else {
        setMinAttendeeNum(parseInt(value));
      }
    }
  };

  useEffect(() => {
    let updatePolicy: TTimeSlotMinAttendeesPolicy = {...timeSlotMinAttenancePolicyInfo, 
      minAttendeeNum: minAttendeeNum,
      errorType: isMinAttendeeNumError 
        ? [...(timeSlotMinAttenancePolicyInfo.errorType ?? []), ErrorType.ATTENDEE_NUM]  // 에러 타입 추가
        : timeSlotMinAttenancePolicyInfo.errorType?.filter(error => error !== ErrorType.ATTENDEE_NUM) ?? []  // 에러 타입 제거
    };
    onChange(idx, updatePolicy);
  }, [minAttendeeNum]);

  useEffect(() => {
    setMinAttendeeNum(timeSlotMinAttenancePolicyInfo.minAttendeeNum ?? defaultMinAttendeeNum);
  }, []);

  return (
    <Container>
      <SettingSection>
        <TimeInput
          startValue={{
            hour: parseInt(timeSlotMinAttenancePolicyInfo.startTime?.substring(0, 2) ?? ""),
            minutes: parseInt(timeSlotMinAttenancePolicyInfo.startTime?.substring(3, 5) ?? ""),
          }}
          startValueError={timeSlotMinAttenancePolicyInfo.errorType?.includes(ErrorType.TIME) ?? false}
          onChangeStart={(hour, minutes) => {
            let updatePolicy: TTimeSlotMinAttendeesPolicy = {...timeSlotMinAttenancePolicyInfo, 
              startTime: hour.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0"),
            };
            onChange(idx, updatePolicy);
          }}
          endValue={{
            hour: parseInt(timeSlotMinAttenancePolicyInfo.endTime?.substring(0, 2) ?? ""),
            minutes: parseInt(timeSlotMinAttenancePolicyInfo.endTime?.substring(3, 5) ?? ""),
          }}
          endValueError={timeSlotMinAttenancePolicyInfo.errorType?.includes(ErrorType.TIME) ?? false}
          onChangeEnd={(hour, minutes) => {
            let updatePolicy: TTimeSlotMinAttendeesPolicy = {...timeSlotMinAttenancePolicyInfo, 
              endTime: hour.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0"),
            };
            onChange(idx, updatePolicy);
          }}
        />
      </SettingSection>
      <SettingSection>
        <InputContainer>
          <UI.Input
            size="small"
            showPrefixIcon
            prefixIcon="people"
            width={120}
            value={minAttendeeNum !== undefined ? `${minAttendeeNum}` : ""}
            onChange={handleChangeMinAttendeesInput}
            error={isMinAttendeeNumError}
          />
          <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)" style={{ whiteSpace: "nowrap" }}>
            인 이상
          </UI.Typography>
        </InputContainer>
      </SettingSection>
      <ButtonSection>
        <UI.Button
          title="삭제"
          type="outlined"
          size="small"
          primary={false}
          onClick={() => onDelete(idx)}
        />
      </ButtonSection>
    </Container>
  );
};

export default MinimumAttendeesSettingItem;



//INFO: 최소 참석 인원 설정 모달에서 시간대 선택으로 사용되는 컴포넌트
export const TimeInput = ({
  startValue,
  startValueError,
  onChangeStart,
  endValue,
  endValueError,
  onChangeEnd,
}: {
  startValue?: { hour?: number; minutes?: number };
  startValueError?: boolean;
  onChangeStart: (hour: number, minutes: number) => void;
  endValue?: { hour?: number; minutes?: number };
  endValueError?: boolean;
  onChangeEnd: (hour: number, minutes: number) => void;
}) => {
  const [openStartTimeDropbox, setOpenStartTimeDropbox] = useState<boolean>(false);
  const [openEndTimeDropbox, setOpenEndTimeDropbox] = useState<boolean>(false);

  useEffect(() => {
    //INFO: 시작 시간이 종료 시간보다 늦을 경우 종료 시간을 시작 시간보다 30분 뒤로 설정
    if (startValue && endValue 
      && startValue.hour && endValue.hour) {
      if (startValue.hour > endValue.hour) {
        if (startValue.minutes === 30) {
          onChangeEnd(startValue.hour + 1, 0);
        } else {
          onChangeEnd(startValue.hour, 30);
        }
      } else if (startValue.hour === endValue.hour && (startValue?.minutes ?? 0) > (endValue?.minutes ?? 0)) {
        if (startValue.minutes === 30) {
          onChangeEnd(startValue.hour + 1, 0);
        } else {
          onChangeEnd(startValue.hour, 30);
        }
      } else if (endValue.hour === startValue.hour && endValue.minutes === startValue.minutes) {
        //INFO: 시작 시간과 종료 시간이 같을 경우 종료 시간을 30분 뒤로 설정
        onChangeEnd(
          startValue.minutes === 30 ? startValue?.hour + 1 : startValue?.hour,
          startValue?.minutes === 30 ? 0 : 30
        );
      }
    }
  }, [startValue]);


  //INFO: 최소 종료시간 설정
  const getMinTime = () => {
    let newHour = Number(startValue?.hour) || 7;
    let newMinutes = (Number(startValue?.minutes) || 0) + 30;

    if (newMinutes >= 60) {
      newMinutes -= 60;
      newHour += 1;
    }

    let minTime = { hour: newHour, minutes: newMinutes };
    return minTime;
  };

  return (
    <TimeContainer>
      <InputForm className="start-time">
        <div style={{ position: "relative" }} onFocus={() => setOpenStartTimeDropbox(true)}>
          <UI.Input
            size="small"
            prefixIcon="clock"
            showPrefixIcon
            value={startValue?.hour ? `${startValue?.hour.toString().padStart(2, "0")}:${(startValue?.minutes ?? 0).toString().padStart(2, "0")}` : ""}
            placeholder="시작"
            error={startValueError}
          />
          <TimeDropdown
            open={openStartTimeDropbox}
            setOpen={setOpenStartTimeDropbox}
            value={{ hour: startValue?.hour ?? 7, minutes: startValue?.minutes ?? 0 }}
            onChange={(hour, minutes) => {
              onChangeStart(hour, minutes);
              setOpenStartTimeDropbox(false);
            }}
            maxTime={{ hour: 22, minutes: 30 }}
          />
        </div>
      </InputForm>
      <div
        style={{
          width: "12px",
          height: 1,
          backgroundColor: "var(--alias-color-label-normal)",
          alignSelf: "center"
        }}
      />
      <InputForm className="end-time">
        <div style={{ position: "relative" }} onFocus={() => setOpenEndTimeDropbox(true)}>
          <UI.Input
            size="small"
            prefixIcon="clock"
            showPrefixIcon
            width={"100%"}
            value={endValue?.hour ? `${endValue?.hour.toString().padStart(2, "0")}:${(endValue?.minutes ?? 0).toString().padStart(2, "0")}` : ""}
            placeholder="종료"
            error={endValueError}
          />
          <TimeDropdown
            minTime={{ hour: getMinTime().hour ?? 7, minutes: getMinTime().minutes ?? 30 }}
            open={openEndTimeDropbox}
            setOpen={setOpenEndTimeDropbox}
            value={{ hour: endValue?.hour ?? 7, minutes: endValue?.minutes ?? 0 }}
            onChange={(hour, minutes) => {
              onChangeEnd(hour, minutes);
              setOpenEndTimeDropbox(false);
            }}
          />
        </div>
      </InputForm>
    </TimeContainer>
  );
};

//INFO: Time컴포넌트에서 사용되는 TimeDropdown 컴포넌트
const TimeDropdown = ({
  value,
  onChange,
  open,
  setOpen,
  minTime = { hour: 7, minutes: 0 },
  maxTime = { hour: 23, minutes: 0 },
}: {
  value: { hour: number; minutes: number };
  onChange?: (hour: number, minutes: number) => void;
  open: boolean;
  setOpen: (value: boolean) => void;
  minTime?: { hour: number; minutes: number };
  maxTime?: { hour: number; minutes: number };
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const Time = [
    { hour: 7, minutes: 0 },
    { hour: 7, minutes: 30 },
    { hour: 8, minutes: 0 },
    { hour: 8, minutes: 30 },
    { hour: 9, minutes: 0 },
    { hour: 9, minutes: 30 },
    { hour: 10, minutes: 0 },
    { hour: 10, minutes: 30 },
    { hour: 11, minutes: 0 },
    { hour: 11, minutes: 30 },
    { hour: 12, minutes: 0 },
    { hour: 12, minutes: 30 },
    { hour: 13, minutes: 0 },
    { hour: 13, minutes: 30 },
    { hour: 14, minutes: 0 },
    { hour: 14, minutes: 30 },
    { hour: 15, minutes: 0 },
    { hour: 15, minutes: 30 },
    { hour: 16, minutes: 0 },
    { hour: 16, minutes: 30 },
    { hour: 17, minutes: 0 },
    { hour: 17, minutes: 30 },
    { hour: 18, minutes: 0 },
    { hour: 18, minutes: 30 },
    { hour: 19, minutes: 0 },
    { hour: 19, minutes: 30 },
    { hour: 20, minutes: 0 },
    { hour: 20, minutes: 30 },
    { hour: 21, minutes: 0 },
    { hour: 21, minutes: 30 },
    { hour: 22, minutes: 0 },
    { hour: 22, minutes: 30 },
    { hour: 23, minutes: 0 },
  ];

  useEffect(() => {
    const handler = (event: any) => {
      if (open && ref.current && !ref.current.contains(event.target)) {
        setOpen?.(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [open]);

  const filteredTime = Time.filter((item) => {
    if (item.hour < minTime.hour || (item.hour === minTime.hour && item.minutes < minTime.minutes)) {
      return false;
    }
    if (item.hour > maxTime.hour || (item.hour === maxTime.hour && item.minutes > maxTime.minutes)) {
      return false;
    }
    return true;
  });

  useEffect(() => {
    if (open && ref.current) {
      const selectedIndex = filteredTime.findIndex(
        (time) => time.hour === value.hour && time.minutes === value.minutes
      );
      const itemHeight = 40;
      const scrollPosition = selectedIndex * itemHeight - (ref.current.clientHeight / 2 - itemHeight / 2);
      ref.current.scrollTop = scrollPosition;
    }
  }, [open, value]);

  return (
    <TimeDropdownContainer ref={ref} style={{ display: open ? "flex" : "none", width: "100%" }}>
      {Time.filter((item) => {
        if (minTime.hour === item.hour) {
          return minTime.minutes <= item.minutes;
        } else if (maxTime.hour === item.hour) {
          return maxTime.minutes >= item.minutes;
        } else {
          return minTime.hour < item.hour && maxTime.hour > item.hour;
        }
      }).map((time, index) => (
        <TimeDropdownItem key={`${time}-${index}`} onClick={() => onChange?.(time.hour, time.minutes)}>
          <UI.Typography
            type="body01 normal"
            fontStyle={value.hour === time.hour && value.minutes === time.minutes ? "bold" : "regular"}
            color="var(--alias-color-label-normal)"
          >
            {time.hour.toString().padStart(2, "0")}:{time.minutes.toString().padStart(2, "0")}
          </UI.Typography>
        </TimeDropdownItem>
      ))}
    </TimeDropdownContainer>
  );
};